import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import VideoEditingService, { PreviewStatus, VideoClip } from 'services/VideoEditingService';

const videoEditingService = new VideoEditingService();

const POLL_MS = 2000;

/**
 * Pre-warm and follow the transcode of every video clip in a list.
 *
 * A clip is only usable once its preview exists: the stream proxy 425s a video whose
 * transcode isn't DONE, so a player gets nothing and a trimmer never learns the duration.
 * This asks for status with `start=true`, which also queues a transcode for any clip that
 * has never had one, and keeps asking until every clip is `ready` or `error`.
 *
 * Audio is excluded — it plays natively from Drive and never gets transcoded, so asking
 * would poll forever on a status that is never coming.
 *
 * Shared by the Video Editing page and the B-Roll Library's Add clips tab. The status
 * endpoint is keyed by Drive fileId alone, so neither surface needs to own the file: the
 * editor asks about b-roll borrowed from another creator's folder, and the library asks
 * about a folder it has only just been pointed at.
 */
export function useClipPreviewStatus(clips: VideoClip[]): {
  statuses: Record<string, PreviewStatus>;
  errors: Record<string, string>;
  retry: (clip: VideoClip, restartPolling?: boolean) => Promise<void>;
  retryAll: (clips: VideoClip[]) => Promise<void>;
  retryingAll: boolean;
} {
  const [statuses, setStatuses] = useState<Record<string, PreviewStatus>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  // Bumped to restart a poll that has already run itself down to "everything is terminal".
  const [pollVersion, setPollVersion] = useState(0);
  const [retryingAll, setRetryingAll] = useState(false);

  // Keyed on the ids themselves rather than the array, so a caller passing a fresh literal
  // (`[...clips, ...brollClips]`) on every render doesn't restart the poll on every render.
  const idsKey = Array.from(new Set(clips.filter(c => c.mimeType.startsWith('video/')).map(c => c.fileId)))
    .sort()
    .join(',');
  const videoIds = useMemo(() => (idsKey ? idsKey.split(',') : []), [idsKey]);

  useEffect(() => {
    if (videoIds.length === 0) { setStatuses({}); setErrors({}); return undefined; }
    let stopped = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    async function poll() {
      const resp = await videoEditingService.getPreviewStatus(videoIds, true);
      if (stopped) return;
      if (resp) {
        setStatuses(resp.statuses);
        setErrors(resp.errors || {});
      }
      const done = resp && videoIds.every(id => resp.statuses[id] === 'ready' || resp.statuses[id] === 'error');
      if (!done) timer = setTimeout(poll, POLL_MS);
    }
    poll();
    return () => { stopped = true; if (timer) clearTimeout(timer); };
  }, [videoIds, pollVersion]);

  const retry = useCallback(async (clip: VideoClip, restartPolling = true): Promise<void> => {
    setStatuses(current => ({ ...current, [clip.fileId]: 'pending' }));
    setErrors((current) => {
      const next = { ...current };
      delete next[clip.fileId];
      return next;
    });
    try {
      await videoEditingService.retryClipPreview(clip.fileId);
      setStatuses(current => ({ ...current, [clip.fileId]: 'transcoding' }));
      if (restartPolling) setPollVersion(current => current + 1);
    } catch (err: any) {
      const message = err?.response?.data?.error || err?.message || 'The preview retry could not be started.';
      setStatuses(current => ({ ...current, [clip.fileId]: 'error' }));
      setErrors(current => ({ ...current, [clip.fileId]: message }));
      throw err;
    }
  }, []);

  // Read through a ref so `retryAll` doesn't change identity every time a status does —
  // callers pass it straight to a button, and a new function each poll tick would re-render
  // the whole clip list twice a second while previews are preparing.
  const statusesRef = useRef(statuses);
  statusesRef.current = statuses;

  const retryAll = useCallback(async (candidates: VideoClip[]): Promise<void> => {
    const failed = candidates.filter(clip => (
      clip.mimeType.startsWith('video/') && statusesRef.current[clip.fileId] === 'error'
    ));
    if (failed.length === 0 || retryingAll) return;
    setRetryingAll(true);
    try {
      // Unbounded on purpose: each retry is one cheap DB write plus an enqueue, and the
      // transcode queue does its own throttling. Restart the poll once for the batch,
      // not once per clip.
      const results = await Promise.allSettled(failed.map(clip => retry(clip, false)));
      if (results.some(result => result.status === 'fulfilled')) {
        setPollVersion(current => current + 1);
      }
    } finally {
      setRetryingAll(false);
    }
  }, [retry, retryingAll]);

  return { statuses, errors, retry, retryAll, retryingAll };
}

export default useClipPreviewStatus;
